import StateIdentifiers from '@/plugins/load-state/state-identifiers';
import timezones from '@/data/timezones';
import moment from 'moment';
import Cookies from 'js-cookie';
import PreferenceTypes from '@/store/preference-types';

export default ({
    apiHandler,
    fullStory,
    toast,
    auth,
    isSandbox,
    isLocal,
}) => ({
    namespaced: true,
    state: {
        isSandbox,
        isLocal,
        browserTimezone: null,
    },
    getters: {
        hasProfileTimezoneDiff: (state, getters, rootState, rootGetters) => !!(rootGetters['user/getUserPreference'](PreferenceTypes.timezone) !== state.browserTimezone),
    },
    mutations: {
        setBrowserTimezone: (state, payload) => {
            state.browserTimezone = payload;
        },
    },
    actions: {
        /**
         * Initialize third-party applications that need to block
         * execution of the application. This always happens first, and on every
         * subsequent login and logout.
         * @param {Function} dispatch
         * @param {Object} rootGetters
         * @param {boolean} preInit defines whether the initialization happens
         * globally from the main boostrap or from within another store call
         * @returns {Promise<void>}
         */
        async initialize({dispatch, rootGetters}, {preInit = false} = {}) {
            if (preInit && window.location.pathname.includes('impersonate')) {
                // when impersonating we do not want to do the init instantly
                // and will trigger it from the `impersonate` method itself
                return;
            }

            dispatch('loading/start', StateIdentifiers.application, {root: true});
            dispatch('initializeIntercom', null, {root: true});
            fullStory.initialize(rootGetters);
            await Promise.all([
                // synchronize api and permission state
                dispatch('synchronize'),
            ]);

            dispatch('loading/stop', StateIdentifiers.application, {root: true});
        },
        // Set cookies to the root domain, on each login. The purpose is this are used
        // by the marketing website analytics for some exclusions for non-prospects.
        // See: https://jira.ilabsinc.com/browse/GWY-7293
        setTrackingCookie() {
            const cookieId = 'has-logged-in';
            const cookieParams = {expires: 180, domain: '.rebilly.com'};
            Cookies.remove(cookieId, cookieParams);
            Cookies.set(cookieId, 'true', cookieParams);
        },
        async login({dispatch}, payload) {
            dispatch('setTrackingCookie');
            // save the user information and then initialize the application
            await dispatch('user/setUser', payload, {root: true});
            dispatch('initialize');
        },
        /**
         * Triggered whenever the application needs to logout the current user and reset
         * its third-party libraries.
         * @param {Function} dispatch
         */
        logout({dispatch}) {
            dispatch('loading/start', StateIdentifiers.application, {root: true});
            dispatch('user/logout', null, {root: true});
            dispatch('loading/reset', null, {root: true});
            // re-synchronize state after logout to update permissions
            dispatch('synchronize');
            dispatch('logoutIntercom', null, {root: true});
            fullStory.logout();
            // reset session storage to prevent data leaks
            sessionStorage.clear();
            // clear router after login redirect path
            dispatch('router/clear', null, {root: true});
        },
        /**
         * Synchronize the API session token and active permissions after a user has logged in
         * or logged out.
         * @param {Function} dispatch
         * @param {Object} rootGetters
         * @returns {Promise<void>}
         */
        async synchronize({dispatch, rootGetters}) {
            const isGuest = rootGetters['user/isGuest'];
            if (!isGuest) {
                const isImpersonating = rootGetters['user/isImpersonating'];
                // set token from user data
                apiHandler.setSessionToken(rootGetters['user/token'], isImpersonating);
                const permissions = rootGetters['user/permissions'];
                if (!isImpersonating && permissions === undefined) {
                    dispatch('logout');
                    return;
                }
                auth.$loadPermissions(permissions);

                try {
                    await Promise.all([
                        dispatch('user/fetchProfile', null, {root: true}),
                        dispatch('getBrowserTimezone'),
                    ]);
                } catch (error) {
                    // something happened with the user's token
                    // which returned an error, most likely the session is dead
                    setTimeout(
                        () => toast.negative(
                            `Your session has expired and you have been logged out. Please login again.`,
                            {delay: 10000},
                        ),
                        500,
                    );
                    dispatch('logout');
                }
            } else {
                // reset permissions and API token
                auth.$init();
                apiHandler.setSessionToken(null);
            }
        },
        /**
         * Impersonate a user by using their token to
         * fetch their data and building a payload for
         * the login.
         * @param dispatch
         * @param to
         * @returns {Promise<void>}
         */
        async impersonate({dispatch}, {to}) {
            // get previous user for FullStory report
            // logout previous session
            dispatch('user/logout', null, {root: true});
            const {token} = to.query;
            // set token from user data
            apiHandler.setSessionToken(token, true);
            // fetch user profile
            try {
                const profile = await apiHandler.client.profile.get();
                const {fields} = profile.getJSON();
                // build login payload
                const user = {
                    email: fields.email,
                    permissions: fields.permissions,
                    rememberMe: false,
                    impersonate: true,
                    token,
                    // auto expire UI session after 1 hour
                    expiredTime: moment.utc().add(1, 'hours').format(),
                };
                // login as the target user,
                // this makes the app consider this
                // a valid user.
                // the `impersonate` flag is thereafter
                // used to check if commits should be ignored
                await dispatch('login', user);
                auth.$loadPermissions(user.permissions);

                setTimeout(
                    () => toast.warning(
                        `Now impersonating account with email <strong>${fields.email}</strong>`,
                        {delay: 10000},
                    ),
                    500,
                );
            } catch (error) {
                console.error(error);
                setTimeout(() => toast.negative('Unable to impersonate user. There was an unexpected error.'), 1000);
            }
        },
        async getBrowserTimezone({commit}) {
            const {fields} = await apiHandler.clientExperimental.location.query();
            commit('setBrowserTimezone', fields.timezone || timezones.default);
        },
    },
});
